import { Dialog, Popup } from 'antd-mobile';
import type { DialogShowHandler } from 'antd-mobile/es/components/dialog';
import {
  type ComponentType,
  type FC,
  type ReactNode,
  useCallback,
  useState,
} from 'react';
import { createRoot } from 'react-dom/client';
import { clearMobileFeedback } from './feedback';
import s from './overlays.module.less';

type CloseableProps = {
  close?: () => void;
};

export type MobileOverlayHandler = {
  close: () => void;
};

const mountedOverlayClosers = new Set<() => void>();

function getDocumentBody() {
  if (typeof document === 'undefined') return null;
  return document.body;
}

function createContainer() {
  const body = getDocumentBody();
  if (!body) return null;

  const container = document.createElement('div');
  body.appendChild(container);
  return container;
}

function registerCloser(close: () => void) {
  mountedOverlayClosers.add(close);
  return () => mountedOverlayClosers.delete(close);
}

export function destroyMobileOverlays() {
  clearMobileFeedback();
  Dialog.clear();
  mountedOverlayClosers.forEach((close) => close());
  mountedOverlayClosers.clear();
}

export function createMobileModalFunction<P extends object>(
  Component: ComponentType<P>,
): FC<P> {
  return ((props: P) => {
    const CloseableComponent = Component as ComponentType<P & CloseableProps>;
    let handler: DialogShowHandler | null = null;
    let unregister = () => {};

    const close = () => {
      unregister();
      handler?.close();
    };

    unregister = registerCloser(close);
    handler = Dialog.show({
      closeOnMaskClick: true,
      content: (
        <div className={s.modalContent}>
          <CloseableComponent {...props} close={close} />
        </div>
      ),
      onClose: close,
    });

    return { close };
  }) as unknown as FC<P>;
}

type FunctionPopupOptions = {
  position?: 'bottom' | 'left' | 'right' | 'top';
};

export function createMobilePopupFunction<P extends object>(
  Component: ComponentType<P>,
  options: FunctionPopupOptions = {},
): FC<P> {
  return ((props: P) => {
    const CloseableComponent = Component as ComponentType<P & CloseableProps>;
    const container = createContainer();
    if (!container) return { close: () => {} };

    const root = createRoot(container);
    const position = options.position || 'bottom';
    let closed = false;

    const close = () => {
      if (closed) return;
      closed = true;
      unregister();
      root.unmount();
      container.remove();
    };
    const unregister = registerCloser(close);

    root.render(
      <Popup
        bodyClassName={[
          s.drawerBody,
          position === 'bottom' ? s.drawerBottom : s.drawerSide,
        ].join(' ')}
        closeOnMaskClick
        onClose={close}
        position={position}
        showCloseButton
        visible
      >
        <CloseableComponent {...props} close={close} />
      </Popup>,
    );

    return { close };
  }) as unknown as FC<P>;
}

export function createMobileModalComponent<P extends object>(
  Component: ComponentType<P>,
): FC<P> {
  return function MobileModalComponent(props: P) {
    const CloseableComponent = Component as ComponentType<P & CloseableProps>;
    const [visible, setVisible] = useState(true);
    const close = useCallback(() => setVisible(false), []);

    return (
      <Dialog
        closeOnMaskClick
        content={
          <div className={s.modalContent}>
            <CloseableComponent {...props} close={close} />
          </div>
        }
        onClose={close}
        visible={visible}
      />
    );
  };
}

export function createMobileDrawerComponent<P extends object>(
  Component: ComponentType<P>,
): FC<P> {
  return function MobileDrawerComponent(props: P) {
    const CloseableComponent = Component as ComponentType<P & CloseableProps>;
    const [visible, setVisible] = useState(true);
    const close = useCallback(() => setVisible(false), []);

    return (
      <Popup
        bodyClassName={[s.drawerBody, s.drawerBottom].join(' ')}
        closeOnMaskClick
        onClose={close}
        showCloseButton
        visible={visible}
      >
        <CloseableComponent {...props} close={close} />
      </Popup>
    );
  };
}

export type MobileResultProps = {
  actions?: ReactNode;
  description?: ReactNode;
  icon?: ReactNode;
  status?: 'error' | 'info' | 'success' | 'warning' | 'waiting';
  title: ReactNode;
};
